export * from "./address";
export * from "./account-performance";
export * from "./alerting";
export * from "./compute-twr-from-series";
export * from "./asset";
export * from "./asset-id-parser";
export * from "./async-utils";
export * from "./bigint";
export * from "./bytes";
export * from "./chain-derivation";
export * from "./command-palette-search";
export * from "./group";
export * from "./hash-utils";
export * from "./helper-types";
export * from "./lru";
export * from "./matcher-event-type";
export * from "./nanoid";
export * from "./performance-trend";
export * from "./privy-wallet";
export * from "./replace-bigints";
export * from "./retry";
export * from "./rrule-utils";
export * from "./runtime";
export * from "./schema";
export * from "./solana-paymaster";
export * from "./strategy-schedule-utils";
export * from "./strategy-schedule-validation";
export * from "./timeouts";
export * from "./timestamp-to-date";
export * from "./trade-color-formatter";
export * from "./triggerable-promise";
export * from "./wait";

export const shortenAddress = (
  address: string,
  startLength = 6,
  endLength = 4,
) => {
  return `${address.slice(0, startLength)}…${address.slice(-endLength)}`;
};

const truncateRegex = /^(0x[a-zA-Z0-9]{2})[a-zA-Z0-9]+([a-zA-Z0-9]{3})$/;
export const truncateEthAddress = (address?: string, separator = "•••") => {
  if (!address) return "";
  const match = address.match(truncateRegex);
  if (!match) {
    return shortenAddress(address, 4, 4);
  }
  return `${match[1]}${separator}${match[2]}`;
};

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function stripSpecialCharacters(inputString: string) {
  // Use a regular expression to replace all non-alphanumeric characters except hyphen, space, dot,and parentheses with an empty string
  return inputString?.replace(/[^a-zA-Z0-9\s.()-]/g, "");
}

export function shuffle(array: any) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export enum FileType {
  Pdf = "application/pdf",
  Heic = "image/heic",
}

/**
 * Fetches the git commit sha for codebase if available. If not, returns null.
 * Works in Glider containers, Render, and Vercel. Local returns null.
 * @returns the commit sha from the environment variable
 */
export const getCommitShaFromEnvVar = () => {
  return (
    process.env.COMMIT_SHA ||
    process.env.RENDER_GIT_COMMIT ||
    process.env.VERCEL_GIT_COMMIT_SHA ||
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
    process.env.VERCEL_DEPLOYMENT_ID ||
    process.env.VERCEL_ENV ||
    // process.env.NODE_ENV ||
    null
  );
};

export const isSupportedFilePreview = (type: FileType) => {
  if (!type) {
    return false;
  }

  if (type === FileType.Heic) {
    return false;
  }

  if (type?.startsWith("image")) {
    return true;
  }

  switch (type) {
    case FileType.Pdf:
      return true;
    default:
      return false;
  }
};

export { removeUndefinedValues } from "./remove-undefined-values";

export function getRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  const base64 = Buffer.from(bytes).toString("base64");
  return base64.replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

export function hexToBytes(hex: string): Uint8Array {
  return new Uint8Array(
    hex.match(/.{1,2}/g)!.map((byte) => Number.parseInt(byte, 16)),
  );
}

export function bytesToHex(bytes: Uint8Array): string {
  return bytes.reduce(
    (hex, byte) => hex + byte.toString(16).padStart(2, "0"),
    "",
  );
}

export function sanitizeUserId(userId: string): string {
  return userId.replace("did:privy:", "");
}

export function generateRandomString(length = 6) {
  const characterSet = "abcdefghijkmnopqrstuvwxyz23456789";
  let randomString = "";

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characterSet.length);
    randomString += characterSet.charAt(randomIndex);
  }

  return randomString;
}

export const isNonNull = <T>(value: T | null): value is T => value !== null;

export function refineNonNull<T>(
  input: T | null | undefined,
  errorMessage?: string,
): T {
  if (input == null) {
    throw new Error(errorMessage ?? `Unexpected ${JSON.stringify(input)}`);
  }

  return input;
}

export const base64ToUint8Array = (base64: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  return new Uint8Array(Buffer.from(b64, "base64"));
};

export const generateRandomBuffer = (): ArrayBuffer => {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return arr.buffer;
};

export const base64UrlEncode = (challenge: ArrayBuffer): string => {
  return Buffer.from(challenge)
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

export function jsonStringifySafe(
  value: any,
  replacer?: (this: any, key: string, value: any) => any,
  space?: string | number,
): string {
  return JSON.stringify(
    value,
    (key, value) => {
      if (typeof value === "bigint") {
        return value.toString();
      }
      if (replacer) {
        return replacer(key, value);
      }
      return value;
    },
    space,
  );
}

export const isEmptyObject = (obj: Record<string, any>): boolean => {
  if (!obj || typeof obj !== "object") return false;
  return Object.keys(obj).length === 0;
};
